/**
 * SalesFollowupSettings — "Sales" section inside Settings → Email Settings.
 * Configures automatic proposal follow-up emails (bid_submitted proposals).
 */
import { useState, useEffect } from "react";
import { tr } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Send, MailCheck, RefreshCw } from "lucide-react";
import { getProposalFollowupSettings, updateProposalFollowupSettings, processProposalFollowups, getReplyDetectionStatus, runReplyScan, getM365AuthUrl } from "@/lib/api";

export default function SalesFollowupSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [reply, setReply] = useState(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    getProposalFollowupSettings().then(r => setS(r.data)).catch(() => toast.error("Failed to load follow-up settings"));
    getReplyDetectionStatus().then(r => setReply(r.data)).catch(() => {});
  }, []);

  const handleConnectM365 = async () => {
    try {
      const res = await getM365AuthUrl();
      if (res.data?.url) window.location.href = res.data.url;
      else toast.error(res.data?.reason || "M365 OAuth not configured");
    } catch { toast.error("Failed to start Microsoft 365 connection"); }
  };

  const handleScanNow = async () => {
    setScanning(true);
    try {
      const res = await runReplyScan();
      const d = res.data || {};
      if (d.status === "no_mailboxes") toast.warning("No mailboxes connected yet");
      else if (d.status === "disabled") toast.warning(d.reason || "M365 not configured");
      else toast.success(`Scanned ${ (d.mailboxes || []).length } mailbox(es) — auto-paused ${d.paused} proposal(s)`);
      getReplyDetectionStatus().then(r => setReply(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed");
    } finally { setScanning(false); }
  };

  const set = (k, v) => setS(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateProposalFollowupSettings({
        enabled: !!s.enabled,
        first_days: parseInt(s.first_days) || 7,
        frequency_days: parseInt(s.frequency_days) || 7,
        max_followups: parseInt(s.max_followups) || 3,
        subject_template: s.subject_template,
        body_template: s.body_template,
      });
      setS(res.data);
      toast.success("Sales follow-up settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const res = await processProposalFollowups();
      const d = res.data || {};
      if (d.status === "disabled") toast.warning("Engine is OFF — enable the toggle and save first");
      else toast.success(`Checked ${d.checked} proposals — sent ${d.sent}, skipped ${d.skipped}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Run failed");
    } finally { setRunning(false); }
  };

  if (!s) return null;

  return (
    <div className="space-y-3" data-testid="sales-followup-settings">
      <div className="flex items-center gap-2 pt-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">{tr("Sales")}</span>
        <h4 className="text-sm font-semibold text-slate-700">Proposal Follow-Ups</h4>
      </div>
      <div className="border rounded-lg p-3 bg-slate-50/50 space-y-3">
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={!!s.enabled} onChange={(e) => set("enabled", e.target.checked)} className="h-3.5 w-3.5 accent-emerald-600 rounded" data-testid="followup-enabled" />
            <span className="font-semibold text-sm">Automatic Follow-Up Emails</span>
          </label>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${s.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`} data-testid="followup-status-badge">
            {s.enabled ? "ON" : "OFF"}
          </span>
        </div>
        <div className="text-[10px] text-slate-500">
          Emails the proposal's primary contact on <b>Bid Submitted</b> proposals. Stops automatically when the proposal is won, lost, or cancelled — or flip the per-proposal toggle off once they respond. Updating the bid date restarts the clock.
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600 flex-wrap">
          <span>First follow-up</span>
          <Input type="number" value={s.first_days} onChange={(e) => set("first_days", e.target.value)} className="h-7 w-14 text-xs text-center" data-testid="followup-first-days" />
          <span>days after bid date, then every</span>
          <Input type="number" value={s.frequency_days} onChange={(e) => set("frequency_days", e.target.value)} className="h-7 w-14 text-xs text-center" data-testid="followup-frequency-days" />
          <span>days, max</span>
          <Input type="number" value={s.max_followups} onChange={(e) => set("max_followups", e.target.value)} className="h-7 w-14 text-xs text-center" data-testid="followup-max" />
          <span>follow-ups</span>
        </div>
        <div>
          <Label className="text-[10px] text-slate-500">{tr("Subject Template")}</Label>
          <Input value={s.subject_template} onChange={(e) => set("subject_template", e.target.value)} className="h-7 text-xs" data-testid="followup-subject" />
        </div>
        <div>
          <Label className="text-[10px] text-slate-500">{tr("Body Template")}</Label>
          <Textarea value={s.body_template} onChange={(e) => set("body_template", e.target.value)} rows={5} className="text-xs" data-testid="followup-body" />
          <div className="text-[10px] text-slate-400 mt-1">
            Variables: {"{contact_name}"} {"{customer_name}"} {"{proposal_name}"} {"{total}"} {"{bid_date}"} {"{days_since_bid}"} {"{proposal_link}"} {"{prepared_by}"} {"{company_name}"} {"{company_phone}"} {"{project_address}"}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <Button type="button" size="sm" variant="outline" className="h-7 text-[10px] border-blue-300 text-blue-700 hover:bg-blue-50" disabled={running} onClick={handleRunNow} data-testid="followup-run-now">
            <Send className="w-3 h-3 mr-1" /> {running ? "Running..." : "Run Engine Now"}
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="btn-save-followup-settings">
            {saving ? "Saving..." : "Save Sales Settings"}
          </Button>
        </div>
      </div>

      {/* Reply Detection — auto-shutoff when the customer emails back (iter242) */}
      <div className="border rounded-lg p-3 bg-slate-50/50 space-y-2" data-testid="reply-detection-settings">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <MailCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span className="font-semibold text-sm">Reply Detection</span>
          </div>
          <Button type="button" size="sm" variant="outline" className="h-7 text-[10px]" disabled={scanning} onClick={handleScanNow} data-testid="reply-scan-now">
            <RefreshCw className={`w-3 h-3 mr-1 ${scanning ? "animate-spin" : ""}`} /> {scanning ? "Scanning..." : "Scan Now"}
          </Button>
        </div>
        <div className="text-[10px] text-slate-500">
          Watches connected inboxes every 10 minutes. A proposal's auto follow-ups switch OFF <b>only when the customer replies to that proposal's follow-up email</b> (sender + subject thread must both match) — a contact emailing about something else won't trigger it. A note is added to the proposal and the customer record. Each AM must click Connect below <b>while logged in as themselves</b>.
        </div>
        <div className="space-y-1">
          {(reply?.mailboxes || []).map((m, i) => (
            // eslint-disable-next-line react/no-array-index-key -- small fixed list
            <div key={`rd-${i}`} className="flex items-center justify-between text-xs bg-white border rounded px-2 py-1.5" data-testid={`reply-mailbox-${i}`}>
              <span className="text-slate-700">
                <b>{m.label}</b>{m.email ? <span className="text-slate-400 ml-1.5">{m.email}</span> : <span className="text-rose-500 ml-1.5">no email set in Settings → Account Managers</span>}
              </span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${m.connected ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                {m.connected ? "CONNECTED" : "NOT CONNECTED"}
              </span>
            </div>
          ))}
          {reply && !reply.configured && (
            <div className="text-[10px] text-amber-600">Microsoft 365 OAuth is not configured on this environment.</div>
          )}
        </div>
        <div className="flex items-center justify-between">
          <Button type="button" size="sm" variant="outline" className="h-7 text-[10px] border-blue-300 text-blue-700 hover:bg-blue-50" onClick={handleConnectM365} data-testid="reply-connect-m365">
            Connect Microsoft 365 (this account)
          </Button>
          {reply?.last_run && (
            <span className="text-[10px] text-slate-400">Last scan: {new Date(reply.last_run).toLocaleString()}{reply.last_result ? ` · paused ${reply.last_result.paused}` : ""}</span>
          )}
        </div>
      </div>
    </div>
  );
}
